import React from "react";
import { Link } from "react-router-dom";
import { Search, BookOpen, ScrollText, Compass, ChevronRight } from "lucide-react";

const features = [
  {
    to: "/word",
    icon: Search,
    label: "Word Search",
    desc: "Enter any English word to see every occurrence in Scripture and the underlying Hebrew or Greek term behind it.",
    testid: "feature-word",
  },
  {
    to: "/verse",
    icon: BookOpen,
    label: "Interlinear Verse",
    desc: "Look up a passage and read it word-by-word with the original Hebrew or Greek stacked beneath, like a scholar's lexicon.",
    testid: "feature-verse",
  },
  {
    to: "/dictionary",
    icon: ScrollText,
    label: "Vine's Dictionary",
    desc: "Consult expository entries on biblical words — meanings, senses, and how they are used across the canon.",
    testid: "feature-dictionary",
  },
  {
    to: "/topical",
    icon: Compass,
    label: "Nave's Topical",
    desc: "Explore Scripture by theme. Every subject gathered into sub-topics with the passages that speak to each.",
    testid: "feature-topical",
  },
];

export default function Home() {
  return (
    <div className="space-y-20" data-testid="home-page">
      {/* Hero */}
      <section className="grid md:grid-cols-12 gap-8 md:gap-12 items-end pt-4">
        <div className="md:col-span-8">
          <div className="text-xs uppercase tracking-[0.3em] text-[#78350F] mb-6" data-testid="hero-eyebrow">
            A Study Companion for Serious Readers
          </div>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-stone-900 mb-8">
            Read the Bible <em className="font-serif-display italic text-[#9A3B3B]">in its own words.</em>
          </h1>
          <p className="text-lg text-stone-700 max-w-2xl leading-relaxed">
            Trace English words back to their Hebrew and Greek originals. Study any verse
            with an interlinear lexicon. Consult Vine's-style dictionary entries and
            Nave's-style topical indexes — all in one quiet, unhurried workspace.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/word" data-testid="cta-start-word"
              className="bg-[#9A3B3B] hover:bg-[#7F2F2F] text-white px-8 py-3 tracking-wide transition-colors flex items-center gap-2">
              Begin a word study <ChevronRight size={16} strokeWidth={1.5} />
            </Link>
            <Link to="/verse" data-testid="cta-start-verse"
              className="border border-[#2E4F4F] text-[#2E4F4F] hover:bg-stone-100 px-8 py-3 tracking-wide transition-colors">
              Look up a verse
            </Link>
          </div>
        </div>

        <div className="md:col-span-4 space-y-3 border-l hairline pl-6 pt-2">
          <div className="text-xs uppercase tracking-[0.25em] text-stone-500">Translations</div>
          <div className="space-y-1">
            <div className="font-serif-display text-2xl text-stone-900">KJV</div>
            <div className="text-xs text-stone-500">King James &middot; with Strong's tagging</div>
          </div>
          <div className="space-y-1 pt-3">
            <div className="font-serif-display text-2xl text-stone-900">WEB</div>
            <div className="text-xs text-stone-500">World English Bible</div>
          </div>
          <div className="space-y-1 pt-3">
            <div className="font-serif-display text-2xl text-stone-900">ASV</div>
            <div className="text-xs text-stone-500">American Standard Version</div>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="grid md:grid-cols-2 gap-6 md:gap-8" data-testid="features">
        {features.map(({ to, icon: Icon, label, desc, testid }) => (
          <Link key={to} to={to} data-testid={testid}
            className="group border hairline bg-[#FDFBF7] p-8 md:p-10 hover:-translate-y-0.5 hover:border-stone-400 transition-all">
            <div className="flex items-start justify-between mb-6">
              <Icon size={22} strokeWidth={1.5} className="text-[#2E4F4F]" />
              <ChevronRight size={16} strokeWidth={1.5} className="text-stone-400 group-hover:text-[#9A3B3B] group-hover:translate-x-1 transition-all" />
            </div>
            <h3 className="font-serif-display text-3xl text-stone-900 mb-3">{label}</h3>
            <p className="text-sm text-stone-600 leading-relaxed">{desc}</p>
          </Link>
        ))}
      </section>

      {/* Quote block */}
      <section className="border-t hairline pt-16">
        <blockquote className="max-w-3xl">
          <p className="font-serif-display italic text-3xl md:text-4xl text-stone-800 leading-tight">
            "The unfolding of thy words giveth light; it giveth understanding unto the simple."
          </p>
          <footer className="mt-4 text-sm text-stone-500 tracking-wide">— Psalm 119:130</footer>
        </blockquote>
      </section>
    </div>
  );
}
